import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../../muiTheme/colors';
import type { SectionByDocumentIdApiResponse } from '../../services/documents/DocumentRepository';
import useTrackActiveSectionInPreview from './useTrackActiveSectionInPreview';

export const PreviewTableOfContents = ({ sections }: { sections?: SectionByDocumentIdApiResponse[] }) => {
  const { t } = useTranslation();
  const { activeSectionId } = useTrackActiveSectionInPreview(sections);

  const activeSectionStyle = {
    color: 'white',
    backgroundColor: COLORS.appFog800,
  };
  const nonActiveSectionStyle = {
    color: COLORS.appFog700,
    backgroundColor: 'transparent',
  };

  return (
    <Box
      sx={{
        marginLeft: '22px',
        paddingBottom: '1.25rem',
      }}
      data-testid="preview-table-of-contents"
    >
      <Typography
        data-testid="preview-table-of-contents-header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          paddingTop: '4px',
          paddingBottom: '4px',
          backgroundColor: COLORS.appFog100,
          color: COLORS.appFog700,
          fontSize: '16px',
          fontWeight: 700,
          lineHeight: '24px',
          textTransform: 'uppercase',
        }}
      >
        {t('document.preview.table_of_contents')}
      </Typography>
      {sections?.map((section) => {
        const isActive = activeSectionId === section.id;

        return (
          <Button
            key={section.id}
            className={isActive ? 'active-section' : ''}
            style={{
              display: 'block',
              padding: '4px 8px',
              marginTop: '14px',
              textDecoration: 'none',
            }}
            sx={{
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
              textOverflow: 'ellipsis',
              textDecoration: 'none',
              width: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              ...nonActiveSectionStyle,
              '&.active-section': { ...activeSectionStyle },
              '&:hover': { ...activeSectionStyle },
              '&:hover:link': { ...activeSectionStyle },
              '&.active-section:link': { ...activeSectionStyle },
              '&:link': { ...nonActiveSectionStyle },
            }}
            variant="text"
            href={`#${section.id}`}
            onClick={(event) => {
              event.preventDefault();
              document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            {section.title}
          </Button>
        );
      })}
    </Box>
  );
};
